// [claude-code 2026-03-24] Wired useERScoring into voice pipeline for deterministic tilt detection
// [claude-code 2026-03-12] Single shared voice assistant context — fixes dual-instance bug
//   where HeaderVoiceControl and FintheonComposer each ran independent SpeechRecognition
// [Codex 2026-07-22] Voice control uses the governed Grok Build text route.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useVoiceAssistant } from "../hooks/useVoiceAssistant";
import { useERScoring, type EREvent } from "../hooks/useERScoring";
import { useBackend } from "../lib/backend";
import type { VoiceRuntimeState, MicPermissionState } from "../types/voice";

interface VoiceContextValue {
  enabled: boolean;
  runtimeState: VoiceRuntimeState;
  conversationId: string | null;
  lastUserText: string;
  lastAssistantText: string;
  hasUserRequestActivity: boolean;
  replyAudioEnabled: boolean;
  voiceWidgetHidden: boolean;
  isSupported: boolean;
  micPermission: MicPermissionState;
  setEnabled: (v: boolean) => void;
  setReplyAudioEnabled: (enabled: boolean) => void;
  setVoiceWidgetHidden: (hidden: boolean) => void;
  toggleEnabled: () => void;
  finishCurrentInput: () => void;
  sendText: (
    text: string,
    mode?: "chat" | "infraction",
    options?: { requestOrigin?: "user" | "system" },
  ) => Promise<any>;
  respondToInfraction: (input?: {
    erScore?: number;
    infractionCount?: number;
  }) => Promise<any>;
  cancel: () => void;
}

const VoiceContext = createContext<VoiceContextValue | null>(null);

export function VoiceProvider({ children }: { children: React.ReactNode }) {
  const backend = useBackend();
  const [voiceWidgetHidden, setVoiceWidgetHidden] = useState(false);

  const handleEREvent = useCallback(
    (event: EREvent) => {
      backend.er.postEREvent(event).catch((err: unknown) => {
        console.warn(
          "[VoiceProvider] ER event persistence failed (non-critical):",
          err,
        );
      });
    },
    [backend],
  );

  const erScoring = useERScoring({ onEvent: handleEREvent });

  const voice = useVoiceAssistant({
    onTranscript: erScoring.processTranscript,
  });

  const setEnabled = useCallback(
    (enabled: boolean) => {
      voice.setEnabled(enabled);
    },
    [voice],
  );

  const toggleEnabled = useCallback(() => {
    setEnabled(!voice.enabled);
  }, [setEnabled, voice.enabled]);

  const sendText = useCallback(
    (
      text: string,
      mode?: "chat" | "infraction",
      options?: { requestOrigin?: "user" | "system" },
    ) => {
      return voice.sendText(text, mode, options);
    },
    [voice],
  );

  const finishCurrentInput = useCallback(() => {
    voice.finishCurrentInput();
  }, [voice]);

  const respondToInfraction = useCallback(
    (input?: { erScore?: number; infractionCount?: number }) => {
      return voice.respondToInfraction(input);
    },
    [voice],
  );

  const cancel = useCallback(() => {
    voice.cancel();
  }, [voice]);

  useEffect(() => {
    const handler = () => toggleEnabled();
    window.addEventListener("fintheon:voice-toggle", handler);
    return () => window.removeEventListener("fintheon:voice-toggle", handler);
  }, [toggleEnabled]);

  const value = useMemo<VoiceContextValue>(
    () => ({
      enabled: voice.enabled,
      runtimeState: voice.runtimeState,
      conversationId: voice.conversationId,
      lastUserText: voice.lastUserText,
      lastAssistantText: voice.lastAssistantText,
      hasUserRequestActivity: voice.hasUserRequestActivity,
      replyAudioEnabled: voice.replyAudioEnabled,
      voiceWidgetHidden,
      isSupported: voice.isSupported,
      micPermission: voice.micPermission,
      setEnabled,
      setReplyAudioEnabled: voice.setReplyAudioEnabled,
      setVoiceWidgetHidden,
      toggleEnabled,
      finishCurrentInput,
      sendText,
      respondToInfraction,
      cancel,
    }),
    [
      cancel,
      finishCurrentInput,
      respondToInfraction,
      sendText,
      setEnabled,
      toggleEnabled,
      voice,
      voiceWidgetHidden,
    ],
  );

  return (
    <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>
  );
}

export function useVoice(): VoiceContextValue {
  const ctx = useContext(VoiceContext);
  if (!ctx) throw new Error("useVoice must be used within <VoiceProvider>");
  return ctx;
}
